import {getConfigurationDisclaimer} from '@client/pages/payroll/utils/validationUtils';
import {Card, CardContent} from '@common-web/components/ui/card';
import clsx from 'clsx';
import {Badge} from '@common-web/components/ui/badge';
import {DataTypeBadge} from '@client/pages/payroll/components/DataTypeBadge';
import {BadgeCheck, Info, Lightbulb, Wand2, X, Zap, ChevronRight, AlertCircle} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {ColumnSelect} from '@client/pages/payroll/components/ColumnSelect';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {
  ExtractedColumn,
  ExtractedFile,
  MappingSuggestion,
  MappingViewStyle,
  TargetFieldInfo,
} from '@client/pages/payroll/types';
import {PayrollFileColumnMapping} from '@api/lib/payrollProcessing/types';
import {PayrollTargetField} from '@api/lib/payrollProcessing/constants';
import {COLUMN_TRANSFORMATION_OPTIONS} from '@client/pages/payroll/constants/transformationOptions';
import {useState, memo} from 'react';
import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {toTitleCase} from '@common/lib/utils';
import {ConfiguredColumnPopover} from '@client/pages/payroll/components/ConfiguredColumnPopover';

export const ColumnSelectorCard = memo(
  ({
    targetField,
    mapping,
    combinedColumns,
    extractedFiles,
    onChangeSingleColumn,
    removeColumnMapping,
    viewStyle,
    suggestion,
    onOpenAdvanced,
    error,
    id,
  }: {
    targetField: TargetFieldInfo;
    mapping: PayrollFileColumnMapping | undefined;
    combinedColumns: ExtractedColumn[];
    extractedFiles: ExtractedFile[];
    onChangeSingleColumn: (targetField: PayrollTargetField, fileId: string, index: number) => void;
    removeColumnMapping: (targetField: PayrollTargetField) => void;
    viewStyle: MappingViewStyle;
    suggestion: MappingSuggestion | undefined;
    onOpenAdvanced: (mode: 'deductions' | 'general', targetField: PayrollTargetField) => void;
    error?: string;
    id?: string;
  }) => {
    const [showSuggestionPopover, setShowSuggestionPopover] = useState(false);
    const disclaimer = getConfigurationDisclaimer(targetField.field);

    if (viewStyle === 'list') {
      return (
        <div
          id={id}
          className={clsx(
            'border rounded-lg transition-all my-2 overflow-hidden border-l-4',
            error
              ? 'border-l-red-500 border-red-500/30 bg-white'
              : mapping
                ? 'border-l-emerald border-emerald/20 bg-white'
                : 'border-l-gray-300 border-gray-200 bg-white hover:bg-gray-50',
          )}
        >
          <div className='px-4 py-3 flex items-center gap-3 min-h-14'>
            <div className='min-w-0 shrink-0'>
              <div>
                <div className='font-medium text-internal-brand-navy truncate mb-1.5 flex items-center gap-2'>
                  {targetField.label}
                  {!!error && <AlertCircle className='h-4 w-4 text-red-500' />}
                </div>
                <div className='flex items-center gap-1.5 whitespace-nowrap'>
                  {targetField.isRequired ? (
                    <Badge className='bg-emerald hover:bg-emerald text-white border-0 text-xs'>
                      Required
                    </Badge>
                  ) : (
                    <Badge variant='gray' className='text-xs bg-internal-brand-gray/30'>
                      Optional
                    </Badge>
                  )}
                  <DataTypeBadge dataType={targetField.dataType} />
                </div>
              </div>
            </div>
            <div className={'flex-1 flex items-center justify-end'}>
              {extractedFiles.length === 0 && !mapping ? (
                <div className='text-xs text-internal-brand-gray italic'>Upload files...</div>
              ) : !!mapping ? (
                <div className='text-sm'>
                  <div className='flex items-center gap-2'>
                    <ConfiguredColumnPopover mapping={mapping} extractedFiles={extractedFiles}>
                      {mapping.columnTransformation.type === 'STATIC_VALUE' ? (
                        <Button
                          size='sm'
                          variant='outline'
                          className='py-1.5 px-4 border-2 border-green-700 hover:border-green-700 bg-white hover:bg-green-50 rounded-lg cursor-pointer'
                        >
                          <span className={'max-w-[200px] truncate'}>
                            Static Value: &quot;{mapping.columnTransformation.staticValue}&quot;
                          </span>
                        </Button>
                      ) : mapping.columnTransformation.type !== 'NONE' ? (
                        <Button
                          size='sm'
                          variant='outline'
                          className='py-1.5 px-4 border-2 border-green-700 hover:border-green-700 bg-white hover:bg-green-50 rounded-lg cursor-pointer'
                        >
                          <Zap className='h-3.5 w-3.5' />
                          <span className={'max-w-[200px] truncate'}>
                            {COLUMN_TRANSFORMATION_OPTIONS.find(
                              (option) => option.type === mapping.columnTransformation.type,
                            )?.label || 'Transform'}{' '}
                            ×{' '}
                            {'columns' in mapping.columnTransformation
                              ? mapping.columnTransformation.columns.length
                              : 1}
                          </span>
                        </Button>
                      ) : (
                        <Button
                          size='sm'
                          variant='outline'
                          className='py-1.5 px-4 border-2 border-green-700 hover:border-green-700 bg-white hover:bg-green-50 rounded-lg cursor-pointer'
                        >
                          <span className={'max-w-[200px] truncate'}>
                            {mapping.columnTransformation.column.name}
                          </span>
                          <span className={'max-w-[200px] text-xs truncate text-gray-500'}>
                            ({mapping.columnTransformation.column.fileId})
                          </span>
                        </Button>
                      )}
                    </ConfiguredColumnPopover>
                  </div>
                </div>
              ) : (
                <ColumnSelect
                  columns={combinedColumns}
                  value={undefined}
                  onValueChange={(v) => {
                    const [fileId, idx] = v.includes(':')
                      ? v.split(':')
                      : [extractedFiles[0]?.file.fileId || '', v];
                    onChangeSingleColumn(targetField.field, fileId, Number(idx));
                  }}
                  placeholder='Select column'
                  className='flex-1 border-internal-brand-blue bg-white focus:border-internal-brand-blue-dark focus:ring-blue-200 h-9 text-sm min-w-[200px] max-w-[300px]'
                />
              )}
            </div>
            <div className='flex items-center gap-2'>
              {!!suggestion && !mapping && (
                <Popover open={showSuggestionPopover} onOpenChange={setShowSuggestionPopover}>
                  <PopoverTrigger asChild>
                    <Button
                      size='sm'
                      variant='outline'
                      className='p-1.5 text-yellow-500 border-2 border-yellow-300 bg-yellow-100 hover:bg-yellow-200 rounded-lg transition-colors cursor-help animate-pulse duration-2500'
                      title={'View suggestion'}
                    >
                      <Lightbulb className='h-5 w-5' />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className='w-72 p-3' side='left'>
                    <div className='space-y-2'>
                      <div className='flex items-center gap-2'>
                        <Lightbulb className='h-4 w-4 text-yellow-500' />
                        <span className='text-sm font-medium text-gray-700'>Suggested Mapping</span>
                        <span className='ml-auto text-xs bg-yellow-100 text-yellow-800 rounded-full px-2 py-1'>
                          {Math.round(suggestion.score || 0)}%
                        </span>
                      </div>
                      <div className='text-sm py-2'>
                        <span className='text-internal-brand-navy font-semibold'>
                          {targetField.label}
                        </span>
                        {' → '}
                        <span className='text-internal-brand-navy font-semibold'>
                          {suggestion.name}
                        </span>
                        {extractedFiles.length > 1 && (
                          <span className='text-internal-brand-navy font-normal'>
                            {' '}
                            ({suggestion.fileId})
                          </span>
                        )}
                      </div>
                      <Button
                        size='sm'
                        onClick={() => {
                          onChangeSingleColumn(
                            targetField.field,
                            suggestion.fileId,
                            suggestion.index,
                          );
                          setShowSuggestionPopover(false);
                        }}
                        className='w-full border-internal-brand-blue hover:border-internal-brand-blue-dark text-internal-brand-blue hover:bg-internal-brand-blue hover:text-white'
                        variant='outline'
                      >
                        Apply Suggestion
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {extractedFiles.length > 0 &&
                (targetField.field === 'ADDITIONAL_PRETAX_DEDUCTIONS_TOTAL_PP' ||
                targetField.field === 'POSTTAX_DEDUCTIONS_TOTAL_PP' ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        size='sm'
                        variant='outline'
                        className='p-1.5 h-auto border-internal-brand-blue text-internal-brand-blue hover:bg-internal-brand-blue hover:border-internal-brand-blue-dark hover:text-white'
                        title={
                          mapping && mapping?.columnTransformation?.type !== 'NONE'
                            ? 'Edit in Advanced'
                            : 'Advanced'
                        }
                      >
                        <Wand2 className='h-5 w-5' />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-60 p-2' side='left'>
                      <div className='flex flex-col gap-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          className='justify-between border-internal-brand-blue text-internal-brand-navy hover:bg-internal-brand-blue hover:border-internal-brand-blue-dark hover:text-white'
                          onClick={() => onOpenAdvanced('general', targetField.field)}
                        >
                          Advanced Editor
                          <ChevronRight className={'w-5 h-5'} />
                        </Button>
                        <Button
                          variant='outline'
                          size='sm'
                          className='justify-between border-internal-brand-blue text-internal-brand-navy hover:bg-internal-brand-blue hover:border-internal-brand-blue-dark hover:text-white'
                          onClick={() => onOpenAdvanced('deductions', targetField.field)}
                        >
                          {mapping && mapping.columnTransformation?.type === 'COLUMN_ADDITION'
                            ? 'Add deduction columns'
                            : 'Combine deduction columns'}
                          <ChevronRight className={'w-5 h-5'} />
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => {
                      onOpenAdvanced('general', targetField.field);
                    }}
                    className='p-1.5 h-auto border-internal-brand-blue text-internal-brand-blue hover:bg-internal-brand-blue hover:border-internal-brand-blue-dark hover:text-white'
                    title={
                      mapping && mapping?.columnTransformation?.type !== 'NONE'
                        ? 'Edit in Advanced'
                        : 'Advanced'
                    }
                  >
                    <Wand2 className='h-5 w-5' />
                  </Button>
                ))}
              {extractedFiles.length > 0 && !!mapping && (
                <Button
                  size='sm'
                  variant='outline'
                  onClick={() => {
                    removeColumnMapping(targetField.field);
                  }}
                  className='p-1.5 h-auto border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400'
                  title='Clear mapping'
                >
                  <X className='h-5 w-5' />
                </Button>
              )}
            </div>
          </div>
          {!!disclaimer && (
            <div className='px-4 pb-3'>
              <Alert variant='default' className='border-blue-200 bg-blue-50'>
                <AlertDescription className='text-xs text-blue-800'>{disclaimer}</AlertDescription>
              </Alert>
            </div>
          )}
          {!!error && (
            <div className='px-4 pb-3'>
              <div className='text-xs text-red-600 font-medium'>{error}</div>
            </div>
          )}
        </div>
      );
    }

    return (
      <Card
        id={id}
        key={targetField.field}
        className={clsx(
          'shadow-sm hover:shadow-md transition-shadow',
          error ? 'border-red-300 bg-red-50' : 'border-gray-200',
        )}
      >
        <CardContent className='p-6'>
          <div className='space-y-6'>
            <div className='flex flex-wrap items-center gap-2'>
              <div className='font-semibold text-internal-brand-navy mr-2 flex items-center gap-2'>
                {targetField.label}
                {!!error && <AlertCircle className='h-4 w-4 text-red-500' />}
              </div>
              <div className='flex flex-row items-center gap-2'>
                {targetField.isRequired ? (
                  <Badge className='bg-emerald hover:bg-emerald text-white border-0 text-xs'>
                    Required
                  </Badge>
                ) : (
                  <Badge variant='secondary' className='text-xs'>
                    Optional
                  </Badge>
                )}
                <DataTypeBadge dataType={targetField.dataType} />
              </div>
            </div>
            <div>
              {extractedFiles.length === 0 && !mapping ? (
                <div className='text-sm text-internal-brand-gray italic p-4 bg-gray-50 rounded-lg text-center'>
                  Upload and extract payroll files to select columns
                </div>
              ) : !!mapping ? (
                <ConfiguredColumnPopover mapping={mapping} extractedFiles={extractedFiles}>
                  <div className='p-4 bg-green-50/50 rounded-lg border-2 border-green-700 hover:border-green-700 cursor-pointer hover:bg-green-50 transition-colors'>
                    <div className='flex items-center gap-2 mb-2'>
                      <BadgeCheck className='h-4 w-4 text-green-800' />
                      <span className='text-sm font-medium text-green-800'>Configured</span>
                      <span className='text-xs text-green-700 ml-auto'>Click to view details</span>
                    </div>
                    <div className='text-sm text-gray-700 flex-1'>
                      <div className='font-medium'>
                        {mapping.columnTransformation.type === 'STATIC_VALUE'
                          ? `Static Value: "${mapping.columnTransformation.staticValue}"`
                          : mapping.columnTransformation.type === 'NONE' &&
                              mapping.columnTransformation.column
                            ? `${mapping.columnTransformation.column.fileId} → ${mapping.columnTransformation.column.name}`
                            : COLUMN_TRANSFORMATION_OPTIONS.find(
                                (option) => option.type === mapping.columnTransformation.type,
                              )?.label ||
                              toTitleCase(mapping.columnTransformation.type.replace(/_/g, ' '))}
                      </div>
                      {mapping.columnTransformation.type !== 'NONE' &&
                        mapping.columnTransformation.type !== 'PIVOT_COLUMN' &&
                        mapping.columnTransformation.type !== 'STATIC_VALUE' &&
                        'columns' in mapping.columnTransformation &&
                        mapping.columnTransformation.columns.length > 0 && (
                          <div className='text-xs text-black mt-1'>
                            {mapping.columnTransformation.columns.length} source columns
                          </div>
                        )}
                      {mapping.columnTransformation.type === 'PIVOT_COLUMN' && (
                        <div className='text-xs text-black mt-1'>
                          Pivot: {mapping.columnTransformation.pivotColumn.name}
                        </div>
                      )}
                      {mapping.valueTransformations && mapping.valueTransformations.length > 0 && (
                        <div className='text-xs text-black mt-1'>
                          {mapping.valueTransformations.length} transformation
                          {mapping.valueTransformations.length !== 1 ? 's' : ''} applied
                        </div>
                      )}
                    </div>
                  </div>
                </ConfiguredColumnPopover>
              ) : (
                <div className='space-y-4'>
                  <ColumnSelect
                    columns={combinedColumns}
                    value={undefined}
                    onValueChange={(v) => {
                      const [fileId, idx] = v.includes(':')
                        ? v.split(':')
                        : [extractedFiles[0]?.file.fileId || '', v];
                      onChangeSingleColumn(targetField.field, fileId, Number(idx));
                    }}
                    placeholder='Select column'
                    className='flex-1 border-internal-brand-blue bg-white focus:border-internal-brand-blue-dark focus:ring-blue-200 h-10'
                  />
                  {!!suggestion && !mapping && (
                    <div className='p-3 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-between flex-wrap gap-2'>
                      <div>
                        <div className='text-sm font-medium text-gray-500 mb-2 flex items-center flex-wrap gap-2'>
                          <Lightbulb className={'text-yellow-500 w-5 h-5'} />{' '}
                          <span>Suggested Mapping</span>{' '}
                          <span
                            className={
                              'text-xs bg-white rounded-full py-1 px-3 border border-gray-200 whitespace-nowrap'
                            }
                          >
                            {Math.round(suggestion.score || 0)}% confidence
                          </span>
                        </div>
                        <div className='text-sm p-1 text-internal-brand-navy font-semibold mt-1'>
                          <span>{targetField.label}</span> → <span>{suggestion.name}</span>
                        </div>
                      </div>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() =>
                          onChangeSingleColumn(
                            targetField.field,
                            suggestion.fileId,
                            suggestion.index,
                          )
                        }
                        className='border-internal-brand-blue hover:border-internal-brand-blue-dark text-internal-brand-blue hover:bg-internal-brand-blue hover:text-white'
                      >
                        Apply
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className='space-y-3'>
              {extractedFiles.length > 0 && !!mapping && (
                <Button
                  size='sm'
                  onClick={() => {
                    removeColumnMapping(targetField.field);
                  }}
                  className='w-full'
                >
                  Clear Mapping
                </Button>
              )}
              {(targetField.field === 'ADDITIONAL_PRETAX_DEDUCTIONS_TOTAL_PP' ||
                targetField.field === 'POSTTAX_DEDUCTIONS_TOTAL_PP') && (
                <Button
                  size='sm'
                  onClick={() => {
                    onOpenAdvanced('deductions', targetField.field);
                  }}
                  className='w-full'
                >
                  {extractedFiles.length > 0 &&
                  !!mapping &&
                  mapping.columnTransformation?.type === 'COLUMN_ADDITION'
                    ? 'Add deduction columns'
                    : 'Combine deduction columns'}
                </Button>
              )}
              <Button
                variant='outline'
                size='sm'
                onClick={() => {
                  onOpenAdvanced('general', targetField.field);
                }}
                className='w-full border-internal-brand-blue text-internal-brand-blue hover:bg-internal-brand-blue hover:border-internal-brand-blue-dark hover:text-white'
              >
                <Wand2 className='h-4 w-4 mr-1' />
                {extractedFiles.length > 0 &&
                !!mapping &&
                mapping?.columnTransformation?.type !== 'NONE'
                  ? 'Edit in Advanced'
                  : 'Advanced'}
              </Button>
            </div>
          </div>
          {!!disclaimer && (
            <div className={'w-full mt-4'}>
              <Alert variant='default' className='border-blue-200 bg-blue-50'>
                <Info className='h-4 w-4 text-blue-600' />
                <AlertDescription className='text-sm text-blue-800'>{disclaimer}</AlertDescription>
              </Alert>
            </div>
          )}
          {!!error && (
            <div className='w-full mt-4 p-3 bg-red-100 rounded-lg border border-red-200 text-sm text-red-700 font-medium'>
              {error}
            </div>
          )}
        </CardContent>
      </Card>
    );
  },
);
